import { Component, type ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { LocationProvider, Router, Route, ErrorBoundary, lazy } from 'preact-iso';
import { AuthProvider } from './state/auth';
import { ToastProvider } from './state/toast';
import { GeoProvider } from './state/geo';
import { TopBar, TabBar, Footer } from './components/Shell';
import { LoginSheet } from './components/LoginSheet';
import { PolicyGate } from './components/PolicyGate';
import { DemographicsGate } from './components/DemographicsGate';
import { Skeleton } from './components/ui';
import { handleInPath } from './lib/i18n';
import { ROUTER_SCOPE } from './lib/router-scope';

// Pages are code-split; each is a small module.
const Home = lazy(() => import('./pages/home'));
const SessionsList = lazy(() => import('./pages/sessions/list'));
const SessionNew = lazy(() => import('./pages/sessions/new'));
const SessionDetail = lazy(() => import('./pages/sessions/detail'));
const CourtsList = lazy(() => import('./pages/courts/list'));
const CourtSuggest = lazy(() => import('./pages/courts/suggest'));
const CourtDetail = lazy(() => import('./pages/courts/detail'));
const CourtClaim = lazy(() => import('./pages/courts/claim'));
const PartnerHome = lazy(() => import('./pages/partner/index'));
const PartnerCourt = lazy(() => import('./pages/partner/court'));
const PartnerEventNew = lazy(() => import('./pages/partner/event-new'));
const BookCourt = lazy(() => import('./pages/bookings/book'));
const BookingDetail = lazy(() => import('./pages/bookings/detail'));
const MyBookings = lazy(() => import('./pages/bookings/mine'));
const ClubsList = lazy(() => import('./pages/clubs/list'));
const ClubNew = lazy(() => import('./pages/clubs/new'));
const ClubDetail = lazy(() => import('./pages/clubs/detail'));
const ClubManage = lazy(() => import('./pages/clubs/manage'));
const Players = lazy(() => import('./pages/players/list'));
const Profile = lazy(() => import('./pages/players/profile'));
const SessionPreview = lazy(() => import('./pages/me/session-preview'));
const MeNotifications = lazy(() => import('./pages/me/notifications'));
const MeDashboard = lazy(() => import('./pages/me/dashboard'));
const MeSessions = lazy(() => import('./pages/me/sessions'));
const MeClubs = lazy(() => import('./pages/me/clubs'));
const MeProfile = lazy(() => import('./pages/me/profile'));
const MeSettings = lazy(() => import('./pages/me/settings'));
const Onboarding = lazy(() => import('./pages/me/onboarding'));
const StaticPage = lazy(() => import('./pages/static'));
const Admin = lazy(() => import('./pages/admin'));
const PostDetail = lazy(() => import('./pages/posts/detail'));
const Search = lazy(() => import('./pages/search'));
const NotFound = lazy(() => import('./pages/not-found'));

/**
 * `/@handle` cannot be expressed as a preact-iso pattern — a route segment is either a literal or a
 * `:param`, never `@` plus a param — so the default route dispatches it by hand.
 *
 * The path arrives with its language prefix still attached, because that is how every other route is
 * registered (`/players/:handle` AND `/en/players/:handle`). Matching the raw path meant `/@mojo`
 * worked and `/en/@mojo` was a 404 — and since nearly every avatar, name and roster row in the app
 * links to `/@handle`, clicking any player on the English site landed on "page not found".
 */
function CatchAll({ path }: { path?: string }) {
  const handle = handleInPath(path ?? '');
  return handle ? <Profile handle={handle} /> : <NotFound />;
}

function PageFallback() {
  return (
    <main class="page" aria-busy="true">
      <Skeleton h={28} w="50%" />
      <div class="mt-16 stack">
        <Skeleton h={90} />
        <Skeleton h={90} />
        <Skeleton h={90} />
      </div>
    </main>
  );
}

class Boundary extends Component<{ children: ComponentChildren }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <main class="page">
          <div class="errbox" role="alert">
            <span>เกิดข้อผิดพลาดในการแสดงหน้านี้: {this.state.error.message}</span>
            <button type="button" class="btn sm secondary" onClick={() => this.setState({ error: null })}>
              ลองใหม่
            </button>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

/**
 * Every route exists twice: once as written, and once under /en.
 *
 * The language is the address, which is what lets a person share an English page, lets Google keep both
 * versions, and lets the Worker render the right <head> for a crawler. Declaring the pairs here rather
 * than by hand keeps the two from drifting: a route added below is bilingual by construction.
 */
// `any` for the component: every page is a lazy() wrapper with its own param props, and the point of
// the list is the paths, not a type that would have to name all 36 signatures.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ROUTES: { path: string; component: any; props?: Record<string, unknown> }[] = [
  { path: '/', component: Home },
  { path: '/sessions', component: SessionsList },
  { path: '/sessions/new', component: SessionNew },
  { path: '/sessions/:id', component: SessionDetail },
  { path: '/courts', component: CourtsList },
  { path: '/courts/suggest', component: CourtSuggest },
  { path: '/courts/:slug/claim', component: CourtClaim },
  { path: '/courts/:slug/book', component: BookCourt },
  { path: '/bookings/:id/pay', component: BookingDetail, props: { pay: true } },
  { path: '/bookings/:id', component: BookingDetail },
  { path: '/me/bookings', component: MyBookings },
  { path: '/courts/:slug/:tab?', component: CourtDetail },
  { path: '/partner', component: PartnerHome },
  { path: '/partner/:id/events/new', component: PartnerEventNew },
  { path: '/partner/:id/:tab?', component: PartnerCourt },
  { path: '/clubs', component: ClubsList },
  { path: '/clubs/new', component: ClubNew },
  { path: '/clubs/:slug', component: ClubDetail },
  { path: '/clubs/:slug/manage', component: ClubManage },
  { path: '/players', component: Players },
  { path: '/players/:handle', component: Profile },
  { path: '/posts/:id', component: PostDetail },
  { path: '/search', component: Search },
  { path: '/me', component: MeDashboard },
  { path: '/me/session-preview', component: SessionPreview },
  { path: '/me/notifications', component: MeNotifications },
  { path: '/me/sessions', component: MeSessions },
  { path: '/me/clubs', component: MeClubs },
  { path: '/me/profile', component: MeProfile },
  { path: '/me/settings', component: MeSettings },
  { path: '/me/onboarding', component: Onboarding },
  { path: '/how-it-works', component: StaticPage, props: { page: 'how' } },
  { path: '/faq', component: StaticPage, props: { page: 'faq' } },
  { path: '/privacy', component: StaticPage, props: { page: 'privacy' } },
  { path: '/terms', component: StaticPage, props: { page: 'terms' } },
  { path: '/admin/:tab?', component: Admin },
];

function Routes() {
  // preact-iso keeps the previous page visible while a lazy chunk loads; on the very first
  // load there is no previous page, so show a skeleton until the first route commits.
  const [first, setFirst] = useState(false);
  const done = () => setFirst(true);
  return (
    <>
      {!first && <PageFallback />}
      <Router onLoadEnd={done} onRouteChange={done}>
        {ROUTES.flatMap((r) =>
          ['', '/en'].map((prefix) => (
            <Route key={prefix + r.path} path={prefix && r.path === '/' ? prefix : prefix + r.path} component={r.component} {...(r.props ?? {})} />
          )),
        )}
        <Route default component={CatchAll} />
      </Router>
    </>
  );
}

export function App() {
  return (
    <LocationProvider scope={ROUTER_SCOPE}>
      <ToastProvider>
        <AuthProvider>
          <GeoProvider>
            <div class="app">
              <a class="skip-link" href="#main-content">ข้ามไปเนื้อหา</a>
              <TopBar />
              <div id="main-content" tabIndex={-1} />
              <Boundary>
                <ErrorBoundary>
                  <Routes />
                </ErrorBoundary>
              </Boundary>
              <Footer />
              <TabBar />
              <LoginSheet />
              {/* Consent gate renders last so it stacks above every sheet; it hides itself once me.policy_accepted. */}
              <PolicyGate />
              {/* After the consent gate: it only opens once the policy is accepted, so the two never stack. */}
              <DemographicsGate />
            </div>
          </GeoProvider>
        </AuthProvider>
      </ToastProvider>
    </LocationProvider>
  );
}
